import { useEffect, useRef, useCallback, useState } from 'react';

const useIdleTimer = (
  timeout = 2 * 60 * 60 * 1000, // 2 horas por defecto
  onIdle = () => {},
  warningTime = 5 * 60 * 1000, // Advertencia 5 minutos antes
  onWarning = () => {}
) => {
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);
  const onIdleRef = useRef(onIdle);
  const onWarningRef = useRef(onWarning);
  const [isWarningActive, setIsWarningActive] = useState(false);

  // Actualizar las referencias de los callbacks
  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);

  useEffect(() => {
    onWarningRef.current = onWarning;
  }, [onWarning]);

  // Función para resetear el timer
  const resetTimer = useCallback(() => {
    // Limpiar timers existentes
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningRef.current) {
      clearTimeout(warningRef.current);
    }
    
    // Ocultar advertencia si está activa
    setIsWarningActive(false);
    
    // Configurar advertencia
    warningRef.current = setTimeout(() => {
      setIsWarningActive(true);
      onWarningRef.current();
    }, timeout - warningTime);
    
    // Configurar logout automático
    timeoutRef.current = setTimeout(() => {
      onIdleRef.current();
    }, timeout);
  }, [timeout, warningTime]);

  // Eventos que indican actividad del usuario
  const events = [
    'mousedown',
    'mousemove',
    'keypress',
    'scroll',
    'touchstart',
    'click'
  ];

  // Función para manejar la actividad
  const handleActivity = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    // Iniciar el timer
    resetTimer();

    // Agregar event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningRef.current) {
        clearTimeout(warningRef.current);
      }
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [handleActivity, resetTimer, events]);

  // Función pública para resetear manualmente el timer
  return { 
    resetTimer,
    isWarningActive,
    remainingTime: warningTime / 1000 // en segundos
  };
};

export default useIdleTimer;
